<script setup lang="ts">
import type { ShipexkursGraphValue, ShipexkursTabItem } from '~~/types'

const props = defineProps({
  data: {
    type: Array as () => ShipexkursTabItem[],
    default: () => [],
  },
})

// Breite via Container Queries statt fixer Bruchteile: die Tabelle sitzt in
// einer resizable USplitter-Spalte, deren Breite unabhängig vom Viewport ist
// — normale `sm:`/`lg:`-Varianten reagieren nur auf die Fenstergröße, nicht
// auf die tatsächliche Containerbreite. Unterhalb von `@sm` steht jedes Item
// einzeln (volle Breite), von `@sm` bis unter `@lg` sind Third-Items bereits
// halbbreit (2 pro Zeile), erst ab `@lg` drittelbreit (3 pro Zeile).
function itemWidthClass(item: ShipexkursTabItem): string {
  if (item.full) return 'w-full'
  return item.third ? 'w-full @lg:w-1/3 @sm:w-1/2' : 'w-full @sm:w-1/2'
}

type RowWidthFn = (item: ShipexkursTabItem) => number

/**
 * Simuliert das `flex-wrap`-Zeilenumbruchverhalten für eine gegebene
 * Breiten-Funktion (ein Breakpoint-Zustand) und liefert pro Item den
 * Zeilenindex, in dem es tatsächlich landet.
 */
function computeRowIndices(items: ShipexkursTabItem[], widthOf: RowWidthFn): number[] {
  const rows: number[] = []
  let width = 0
  let row = 0

  for (const item of items) {
    const itemWidth = widthOf(item)

    if (width + itemWidth > 1) {
      row++
      width = 0
    }

    rows.push(row)
    width += itemWidth
  }

  return rows
}

// Breite je Item bei @sm bis unter @lg (Third-Items sind hier bereits
// halbbreit, siehe `itemWidthClass`) bzw. ab @lg (Third-Items drittelbreit).
const halfTierWidth: RowWidthFn = (item) => (item.full ? 1 : 1 / 2)
const thirdTierWidth: RowWidthFn = (item) => (item.full ? 1 : item.third ? 1 / 3 : 1 / 2)

const halfTierRows = computed(() => computeRowIndices(props.data, halfTierWidth))
const thirdTierRows = computed(() => computeRowIndices(props.data, thirdTierWidth))

/**
 * Trennlinie als `border-b` auf der Zeile *über* dem Umbruch statt `border-t`
 * auf der Zeile darunter: die obere Zeile ist (bis auf Rundungsfälle) immer
 * vollständig gefüllt, während die untere/letzte Zeile z.B. nur ein einzelnes
 * halb- oder drittelbreites Item enthalten kann — eine Linie darüber würde
 * dann nur über einen Teil der Breite laufen statt über die volle Zeile.
 * Ein Item bekommt die Linie also, wenn NACH ihm noch eine weitere Zeile
 * folgt (`@max-*`/`@`-Varianten sind disjunkte Wertebereiche, konkurrieren
 * also nie um dieselbe Property).
 */
function computeBottomBorderClasses(index: number): string[] {
  const isLast = index === props.data.length - 1
  if (isLast) return []

  // Unterhalb @sm ist jedes Item ohnehin volle Breite (siehe `itemWidthClass`)
  // — eine Linie darunter ist dort immer korrekt, unabhängig von Zeilen.
  const classes: string[] = ['@max-sm:border-b @max-sm:border-primary/30']

  const lastHalfRow = halfTierRows.value[halfTierRows.value.length - 1]
  const lastThirdRow = thirdTierRows.value[thirdTierRows.value.length - 1]

  // Startet die NÄCHSTE Zeile mit einem Full-Width-Item (z.B. eine
  // "MIETEN:"-Kopfzeile), übernimmt dessen `border-t` (s.u.) die Trennung —
  // egal wie viele Items (z.B. dynamische Kauf-Angebote) in dieser Zeile
  // stehen, das Full-Item ist garantiert immer 100% breit, die aktuelle
  // Zeile im Zweifel nicht.
  if (!isRowFollowedByFullRow(halfTierRows.value, index) && halfTierRows.value[index] < lastHalfRow) {
    classes.push('@sm:@max-lg:border-b @sm:@max-lg:border-primary/30')
  }

  if (!isRowFollowedByFullRow(thirdTierRows.value, index) && thirdTierRows.value[index] < lastThirdRow) {
    classes.push('@lg:border-b @lg:border-primary/30')
  }

  return classes
}

/** Erstes Item der Folgezeile (falls vorhanden) — Full-Items belegen immer ihre Zeile allein. */
function isRowFollowedByFullRow(rows: number[], index: number): boolean {
  const nextRowStart = rows.findIndex((row) => row === rows[index] + 1)
  return nextRowStart !== -1 && !!props.data[nextRowStart]?.full
}

/**
 * Gegenstück zu oben: ein Full-Width-Item übernimmt die Trennung von einer
 * ggf. unvollständigen Zeile davor, weil es selbst garantiert die komplette
 * Breite abdeckt. Nötig nur, wenn davor ein Nicht-Full-Item steht — folgen
 * zwei Full-Items aufeinander, hat das erste bereits eine korrekte
 * (volle) `border-b`, eine zusätzliche `border-t` gäbe eine doppelte Linie.
 */
function computeTopBorderClasses(item: ShipexkursTabItem, index: number): string[] {
  const previousItem = props.data[index - 1]
  if (item.full && previousItem && !previousItem.full) {
    return ['border-t border-primary/30']
  }

  return []
}

function itemBorderClass(item: ShipexkursTabItem, index: number): string {
  return [...computeTopBorderClasses(item, index), ...computeBottomBorderClasses(index)].join(' ')
}

// Calcucation if type is graph (min/max/value to percentage)
function valuePercent(value: number, min: number, max: number): number {
  const range = max - min
  if (range <= 0) return 0

  return Math.min(100, Math.max(0, ((value - min) / range) * 100))
}

function graphPercent(item: ShipexkursTabItem): number | null {
  if (!item.graph || !('min' in item) || item.min == null) return null

  return valuePercent(item.value, item.min, item.max)
}

const CIRCLE_RADIUS = 15.5
const CIRCLE_CIRCUMFERENCE = 2 * Math.PI * CIRCLE_RADIUS

function circleDashoffset(item: ShipexkursTabItem): number {
  const percent = graphPercent(item) ?? 0
  return CIRCLE_CIRCUMFERENCE * (1 - percent / 100)
}

// Mehrere Werte in einem Ring (2–4): von außen nach innen gestaffelte Radien
// mit fixem Abstand pro Ring (unabhängig von der Anzahl — sonst reißt der
// Abstand bei z.B. nur 2 Werten unnötig weit auf), jeweils eigener
// Umfang/Dashoffset — Farbe optional pro Wert, sonst Standard-Palette.
const MULTI_RING_MAX_RADIUS = 15
const MULTI_RING_STEP = 3.5
const DEFAULT_RING_COLORS = ['var(--color-aris-400)', 'var(--color-industrial-400)', '#60a5fa', '#c084fc']

function multiRingRadius(index: number): number {
  return MULTI_RING_MAX_RADIUS - index * MULTI_RING_STEP
}

function multiRingCircumference(index: number): number {
  return 2 * Math.PI * multiRingRadius(index)
}

function multiRingDashoffset(entry: ShipexkursGraphValue, index: number): number {
  const percent = valuePercent(entry.value, entry.min, entry.max)
  return multiRingCircumference(index) * (1 - percent / 100)
}

// Nuxt-UI-Semantikfarben (z.B. `color="primary"` bei UBadge/UAlert) werden auf
// ihre CSS-Variable aufgelöst — alles andere (Hex, rgb(), var(...), ...) wird
// unverändert als CSS-Farbe durchgereicht.
const UI_SEMANTIC_COLORS = new Set(['primary', 'secondary', 'success', 'info', 'warning', 'error', 'neutral'])

function resolveColor(color: string | undefined, fallback: string): string {
  if (color && UI_SEMANTIC_COLORS.has(color)) return `var(--ui-${color})`
  return color ?? fallback
}

function ringColor(entry: ShipexkursGraphValue, index: number): string {
  return resolveColor(entry.color, DEFAULT_RING_COLORS[index % DEFAULT_RING_COLORS.length])
}

function singleRingColor(item: ShipexkursTabItem): string {
  return resolveColor('color' in item ? item.color : undefined, 'var(--color-industrial-400)')
}
</script>

<template>
  <div v-if="data.length" class="w-full flex flex-wrap @container">
    <div
      v-for="(item, index) in data"
      :key="index"
      class="py-2"
      :class="[itemWidthClass(item), itemBorderClass(item, index)]"
    >
      <div v-if="item.graph === 'circle' && item.values" class="flex items-center gap-2">
        <svg viewBox="0 0 36 36" class="size-14 shrink-0 -rotate-90">
          <template v-for="(entry, entryIndex) in item.values" :key="entryIndex">
            <circle
              cx="18"
              cy="18"
              :r="multiRingRadius(entryIndex)"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              class="text-white/10"
            />
            <circle
              cx="18"
              cy="18"
              :r="multiRingRadius(entryIndex)"
              fill="none"
              :stroke="ringColor(entry, entryIndex)"
              stroke-width="2.5"
              stroke-linecap="round"
              :stroke-dasharray="multiRingCircumference(entryIndex)"
              :stroke-dashoffset="multiRingDashoffset(entry, entryIndex)"
            />
          </template>
        </svg>

        <div>
          <span class="flex items-center gap-1.5 text-xs font-bold text-muted">
            <ShipexkursSystemIcon v-if="item.icon" :name="item.icon" class="size-3.5 shrink-0 text-aris-400" />
            {{ item.label }}
          </span>

          <span
            v-for="(entry, entryIndex) in item.values"
            :key="entryIndex"
            class="flex items-center gap-1.5 font-mono text-white text-xs"
          >
            <span
              class="size-1.5 shrink-0 rounded-full"
              :style="{ backgroundColor: ringColor(entry, entryIndex) }"
            />
            <span class="text-muted normal-case">{{ entry.label }}</span>
            {{ entry.value }}
          </span>
        </div>
      </div>

      <div v-else class="flex items-center gap-2">
        <svg
          v-if="item.graph === 'circle'"
          viewBox="0 0 36 36"
          class="size-8 shrink-0 -rotate-90"
        >
          <circle
            cx="18"
            cy="18"
            :r="CIRCLE_RADIUS"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            class="text-white/10"
          />
          <circle
            cx="18"
            cy="18"
            :r="CIRCLE_RADIUS"
            fill="none"
            :stroke="singleRingColor(item)"
            stroke-width="3"
            stroke-linecap="round"
            :stroke-dasharray="CIRCLE_CIRCUMFERENCE"
            :stroke-dashoffset="circleDashoffset(item)"
          />
        </svg>

        <div>
          <span class="flex items-center gap-1.5 text-xs font-bold text-muted">
            <ShipexkursSystemIcon v-if="item.icon" :name="item.icon" class="size-3.5 shrink-0 text-aris-400" />
            {{ item.label }}
          </span>

          <span class="base font-mono text-white text-sm uppercase">
            <span v-if="item.prefix" class="">{{ item.prefix }}</span>
            {{ item.value ?? 'N/A' }}
            <span v-if="item.suffix" class="">{{ item.suffix }}</span>
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
